import { ComplianceTemplate } from '@/firebase/complianceService';
import { Location } from '@/firebase/locationService';
import { complianceService } from '@/firebase/complianceService';
import { locationService } from '@/firebase/locationService';

export interface ComplianceTask {
  id: string;
  templateId: string;
  templateName: string;
  locationId: string;
  locationName: string;
  locationState?: string; // Added for state filtering
  category: string;
  frequency: string;
  dueDate: Date;
  leadTimeDays?: number;
  ownerRole: string;
  riskLevel: string;
  evidenceTypes: any[];
  passFailCriteria: string;
  jurisdiction: string;
  jurisdictionNotes: string;
  status: 'pending' | 'completed' | 'overdue' | 'pending_review';
  completedDate?: Date;
  completedBy?: string;
  reviewedBy?: string;
  reviewedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
  // Reference images for task context
  referenceImageUrls?: string[];
  referenceImageFileNames?: string[];
  referenceImageMetadata?: Array<{
    url: string;
    fileName: string;
    uploadedAt: Date | number;
    uploadedBy: string;
    uploadedByName?: string;
    note?: string; // Optional note for the reference image
  }>;
}

// Parse ISO 8601 duration (e.g., P1Y, P1M, P6M)
export function parseFrequency(frequency: string): { years: number; months: number; days: number } {
  const match = frequency.match(/P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)D)?/);
  if (!match) {
    return { years: 0, months: 0, days: 0 };
  }
  return {
    years: parseInt(match[1] || '0', 10),
    months: parseInt(match[2] || '0', 10),
    days: parseInt(match[3] || '0', 10),
  };
}

// Calculate next due date from a base date and frequency
export function calculateNextDueDate(baseDate: Date, frequency: string): Date {
  const { years, months, days } = parseFrequency(frequency);
  const nextDate = new Date(baseDate);
  nextDate.setFullYear(nextDate.getFullYear() + years);
  nextDate.setMonth(nextDate.getMonth() + months);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
}

// Generate compliance tasks for a location based on assigned templates
export async function generateComplianceTasksForLocation(locationId: string, organizationId?: string): Promise<ComplianceTask[]> {
  const locationResult = await locationService.getLocation(locationId);
  if (!locationResult.success || !locationResult.data) {
    return [];
  }

  const location = locationResult.data;
  const tasks: ComplianceTask[] = [];
  const now = new Date();

  if (!location.assignedTemplates || location.assignedTemplates.length === 0) {
    return [];
  }

  // Fetch templates for the specific organization
  const templatesResult = await complianceService.getAllTemplates(organizationId);
  if (!templatesResult.success || !templatesResult.data) {
    return [];
  }

  const templates = templatesResult.data.filter(t => t.id && location.assignedTemplates?.includes(t.id));

  for (const template of templates) {
    // Generate a task for each lead time
    if (!template.lead_times_days || template.lead_times_days.length === 0) {
      continue;
    }
    
    // Calculate base due date: use mark_due_in_days if set, otherwise default to 30 days for backward compatibility
    let baseDueDate: Date;
    const markDueInDays = template.mark_due_in_days ?? 30; // Default to 30 days for backward compatibility
    baseDueDate = new Date(now);
    baseDueDate.setDate(baseDueDate.getDate() + markDueInDays);
    
    for (const leadTimeDays of template.lead_times_days) {
      // Calculate task due date: base due date minus lead time days
      // This creates alerts that appear before the actual compliance due date
      const taskDueDate = new Date(baseDueDate);
      taskDueDate.setDate(taskDueDate.getDate() - leadTimeDays);
      
      // Don't create tasks with due dates in the past (unless they're overdue)
      // If the calculated date is before now, we still create it but mark as overdue
      
      const task: ComplianceTask = {
        id: `${locationId}-${template.id}-${leadTimeDays}`,
        templateId: template.id!,
        templateName: template.name,
        locationId: location.id!,
        locationName: location.name,
        locationState: location.state, // Add state from location
        category: template.category,
        frequency: template.frequency,
        dueDate: taskDueDate,
        leadTimeDays: leadTimeDays,
        ownerRole: template.owner_role,
        riskLevel: template.risk_level,
        evidenceTypes: template.evidence_types,
        passFailCriteria: template.pass_fail_criteria,
        jurisdiction: template.jurisdiction,
        jurisdictionNotes: template.jurisdiction_notes,
        status: taskDueDate < now ? 'overdue' : 'pending',
        createdAt: now,
        updatedAt: now,
      };

      tasks.push(task);
    }
  }

  return tasks;
}

// Generate all compliance tasks for all locations
export async function generateAllComplianceTasks(organizationId?: string): Promise<ComplianceTask[]> {
  const locationsResult = await locationService.getAllLocations(organizationId);
  if (!locationsResult.success || !locationsResult.data) {
    return [];
  }

  const locations = locationsResult.data;
  const allTasks: ComplianceTask[] = [];
  const taskIdSet = new Set<string>(); // Track unique task IDs

  for (const location of locations) {
    const locationTasks = await generateComplianceTasksForLocation(location.id!, organizationId);
    
    // Only add tasks that haven't been added yet
    for (const task of locationTasks) {
      if (!taskIdSet.has(task.id)) {
        allTasks.push(task);
        taskIdSet.add(task.id);
      }
    }
  }

  // Sort by due date
  allTasks.sort((a, b) => a.dueDate.getTime() - b.dueDate.getTime());

  return allTasks;
}

